import i18n from "i18next";
import { useState } from "react";
import { IconX } from "@tabler/icons-react";
import { testIds } from "@/testIds";
import { svgDataUrl } from "@/lib/tikzFigure";
import { addPreambleLines, drawTime, groupPreambleLines, preambleNotice } from "@/lib/tikzAuthoring";
import { TOOLBOX_INSERT_EVENT } from "@/components/toolbox/insertHandler";
import type { PreambleAccess } from "@/components/editor/nodeviews/tikz/useDocumentPreamble";
import {
  addedRanges,
  clearFigureScope,
  contextChipLabel,
  figureSuggestions,
  lineChipLabel,
  lineDiff,
  requestFigureAction,
  setLineHighlight,
  setPendingChange,
  useTikzAsk,
  type FigureLive,
  type FigureScope,
  type FigureSuggestion,
  type PendingChange,
  type TikzAskProposal,
} from "@/lib/tikzAsk";
import styles from "./AskFigure.module.scss";
import { MathText } from "./MathText";

const T = testIds.tikzAsk;

/**
 * Ask Lilia on a figure, in the dock (TikZ step 3, 1c): the context chip, the
 * suggestions by state, and what a figure turn proposes. The panel sends;
 * these show and apply.
 */

/** A figure turn: what was proposed, for which figure, and its source when asked. */
export interface FigureTurn {
  proposal: TikzAskProposal;
  blockId: string | null;
  afterBlockId: string | null;
  /** The figure's source when it was asked (a change's diff, line chips). */
  before: string;
}

/** The figure the dock is scoped to, and what it publishes about itself. */
export function useFigureScope(documentId: string | undefined): { scope: FigureScope | null; live: FigureLive | undefined } {
  const scope = useTikzAsk((s) => (s.scope && s.scope.documentId === documentId ? s.scope : null));
  const live = useTikzAsk((s) => (scope?.blockId ? s.figures[scope.blockId] : undefined));
  return { scope, live };
}

/** "Figure 3 · TikZ" / "New figure · §3.2", with × to leave the figure. */
export function FigureContextChip({ scope, live }: { scope: FigureScope; live: FigureLive | undefined }) {
  return (
    <span className={styles.chip} data-testid={T.contextChip} data-block-id={scope.blockId ?? undefined} title={i18n.t("editor:askFigure.askLiliaReadsThisFigure")}>
      {contextChipLabel(scope, live)}
      <button type="button" className={styles.chipClear} data-testid={T.contextClear} aria-label={i18n.t("editor:askFigure.askAboutTheWholeDocument")} onClick={() => clearFigureScope()}>
        <IconX size={11} />
      </button>
    </span>
  );
}

/** "For this figure": each pre-fills the input; nothing runs until it is sent. */
export function FigureSuggestions({ live, isNew, onPick }: { live: FigureLive | undefined; isNew: boolean; onPick: (s: FigureSuggestion) => void }) {
  const items = figureSuggestions(isNew ? "empty" : (live?.state ?? "drawn"));
  return (
    <div className={styles.suggestions} data-testid="ask-lilia-figure-suggestions">
      <span className={styles.suggestionsTitle}>{i18n.t("editor:askFigure.forThisFigure")}</span>
      {items.map((s) => (
        <button
          key={s.intent}
          type="button"
          className={styles.suggestion}
          data-testid={T.suggestion}
          data-intent={s.intent}
          data-error={s.intent === "fix" ? "true" : undefined}
          onClick={() => onPick(s)}
        >
          <span className={styles.suggestionLabel}>{s.label}</span>
          <span className={styles.suggestionHint}>{s.hint}</span>
        </button>
      ))}
    </div>
  );
}

/** A line chip: hovering highlights the lines (split view or canvas source), clicking scrolls to them. */
export function LineChip({ blockId, from, to, children }: { blockId: string | null; from: number; to: number; children?: React.ReactNode }) {
  const on = () => blockId && setLineHighlight({ blockId, from, to });
  const off = () => setLineHighlight(null);
  return (
    <button
      type="button"
      className={styles.lineChip}
      data-testid={T.aiLineChip}
      data-from={from}
      data-to={to}
      disabled={!blockId}
      onMouseEnter={on}
      onMouseLeave={off}
      onFocus={on}
      onBlur={off}
      onClick={() => blockId && requestFigureAction(blockId, { type: "scroll-to-line", line: from })}
    >
      {children ?? lineChipLabel(from, to)}
    </button>
  );
}

function insertNewFigure(source: string, afterBlockId: string | null, openSplit: boolean) {
  window.dispatchEvent(
    new CustomEvent(TOOLBOX_INSERT_EVENT, {
      detail: {
        call: { type: "figure", content: { kind: "tikz", source, caption: "", alt: "" } },
        source: "toolbox",
        afterBlockId: afterBlockId ?? undefined,
        openSplit,
      },
    }),
  );
}

async function addPreamble(preamble: PreambleAccess, lines: string[] | undefined): Promise<string[]> {
  const missing = (lines ?? []).filter((l) => !preamble.read().includes(l));
  if (!missing.length) return [];
  await preamble.write(addPreambleLines(preamble.read(), missing));
  return missing;
}

/** The pending change a proposal makes in the split view. */
export function pendingFrom(turn: FigureTurn, current: string): PendingChange | null {
  const p = turn.proposal;
  if (!turn.blockId || !p.source) return null;
  const rows = lineDiff(current, p.source);
  return {
    blockId: turn.blockId,
    before: current,
    source: p.source,
    svg: p.svg ? svgDataUrl(p.svg) : null,
    drew: p.kind === "change" || p.kind === "draw",
    drawMs: p.drawMs ?? null,
    changedLines: p.changedLines ?? rows.filter((r) => r.op !== " ").length,
    preambleAdditions: p.preambleAdditions ?? [],
    error: p.error ? { line: p.error.line ?? null, message: p.error.message } : null,
  };
}

function meta(p: TikzAskProposal): string {
  const parts: string[] = [];
  if (p.lineCount) parts.push(p.lineCount === 1 ? "1 line" : `${p.lineCount} lines`);
  const added = groupPreambleLines(p.preambleAdditions ?? []);
  if (added.length) parts.push(`adds ${added.join(" and ")} to the preamble`);
  for (const pkg of p.packages ?? []) parts.push(`uses ${pkg} (loaded for you)`);
  return parts.join(" · ");
}

/**
 * Draw: the drawing card (the SVG, its line count, any preamble addition),
 * Insert figure (solid) · Open in split view (tonal). A draw that failed twice
 * shows its source and Open in split view only.
 */
function DrawingCard({ turn, preamble }: { turn: FigureTurn; preamble: PreambleAccess }) {
  const p = turn.proposal;
  const [done, setDone] = useState<null | "inserted" | "opened">(null);
  const [note, setNote] = useState<string | null>(null);
  const failed = p.kind === "failed";
  const source = p.source ?? "";

  const put = async (open: boolean) => {
    if (turn.blockId) {
      // The empty figure the split view (or the canvas) is showing takes it.
      requestFigureAction(turn.blockId, { type: "set-source", source });
      if (open) requestFigureAction(turn.blockId, { type: "open-split" });
    } else {
      insertNewFigure(source, turn.afterBlockId, open);
      clearFigureScope("insert");
    }
    setDone(open ? "opened" : "inserted");
    try {
      const added = await addPreamble(preamble, p.preambleAdditions);
      if (added.length) setNote(preambleNotice(added));
    } catch {
      setNote(`Couldn't add ${groupPreambleLines(p.preambleAdditions ?? []).join(" and ")} to the preamble. Add it in Document settings.`);
    }
  };

  return (
    <div className={styles.card} data-testid={T.aiProposal} data-kind={p.kind}>
      {failed ? (
        <pre className={styles.source} data-error-line={p.error?.line ?? undefined}>
          {source.split("\n").map((l, i) => (
            <span key={i} className={styles.sourceLine} data-bad={p.error?.line === i + 1 ? "true" : undefined}>
              <span className={styles.sourceNo}>{i + 1}</span>
              {l || " "}
              {"\n"}
            </span>
          ))}
        </pre>
      ) : p.svg ? (
        <div className={styles.paper}>
          <img src={svgDataUrl(p.svg)} alt={i18n.t("editor:askFigure.askLiliaSDrawing")} className={styles.drawing} />
        </div>
      ) : (
        <div className={styles.paperGone}>The drawing isn&apos;t kept with the conversation. Open it in the split view to see it.</div>
      )}
      <div className={styles.cardFoot}>
        {meta(p) && <span className={styles.meta}>{meta(p)}</span>}
        {done ? (
          <span className={styles.done} role="status">
            {done === "inserted" ? "Inserted." : "Opened in the split view."}
            {note ? ` ${note}` : ""}
          </span>
        ) : (
          <div className={styles.actions}>
            {!failed && (
              <button type="button" className={styles.solid} data-testid={T.proposalInsert} onClick={() => void put(false)}>
                {i18n.t("editor:askFigure.insertFigure")}
              </button>
            )}
            <button type="button" className={styles.tonal} data-testid={T.proposalOpenSplit} onClick={() => void put(true)}>
              {i18n.t("editor:askFigure.openInSplitView")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Change: the diff waits in the split view; here, the lines it touched and whether it drew. */
function ChangeCard({ turn, live }: { turn: FigureTurn; live: FigureLive | undefined }) {
  const p = turn.proposal;
  const pending = useTikzAsk((s) => (turn.blockId ? s.pending[turn.blockId] : undefined));
  const ranges = p.source ? addedRanges(lineDiff(turn.before, p.source)) : [];
  const stillPending = pending && pending.source === p.source;
  const reopen = () => {
    if (!turn.blockId) return;
    if (!stillPending) {
      const change = pendingFrom(turn, live?.source ?? turn.before);
      if (change) setPendingChange(change);
    }
    requestFigureAction(turn.blockId, { type: "open-split" });
  };
  return (
    <div className={styles.changeRow} data-testid={T.aiProposal} data-kind={p.kind}>
      {ranges.map((r) => (
        <LineChip key={`${r.from}-${r.to}`} blockId={turn.blockId} from={r.from} to={r.to} />
      ))}
      {p.kind === "change" && (
        <span className={styles.drew}>✓ drew{drawTime(p.drawMs) ? ` in ${drawTime(p.drawMs)}` : ""}</span>
      )}
      {p.kind === "failed" ? (
        <button type="button" className={styles.tonal} data-testid={T.proposalOpenSplit} onClick={reopen}>
          {i18n.t("editor:askFigure.openInSplitView")}
        </button>
      ) : (
        (stillPending || !live?.splitOpen) && (
          <button type="button" className={styles.tonal} data-testid={T.proposalShowChange} onClick={reopen}>
            {stillPending ? "Review the change" : "Show the change again"}
          </button>
        )
      )}
    </div>
  );
}

function CaptionCard({ turn }: { turn: FigureTurn }) {
  const [used, setUsed] = useState(false);
  const caption = turn.proposal.caption ?? "";
  return (
    <div className={styles.card} data-testid={T.aiProposal} data-kind="caption">
      <p className={styles.caption}>{caption}</p>
      <div className={styles.cardFoot}>
        {used ? (
          <span className={styles.done} role="status">{i18n.t("editor:askFigure.captionSet")}</span>
        ) : (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.solid}
              data-testid={T.proposalUseCaption}
              disabled={!turn.blockId}
              onClick={() => {
                if (!turn.blockId) return;
                requestFigureAction(turn.blockId, { type: "set-caption", caption });
                setUsed(true);
              }}
            >
              {i18n.t("editor:askFigure.useThisCaption")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Explain: a line chip for each part; it never edits. */
function ExplainList({ turn }: { turn: FigureTurn }) {
  return (
    <div className={styles.explain}>
      {(turn.proposal.lines ?? []).map((l, i) => (
        <div key={i} className={styles.explainRow}>
          <LineChip blockId={turn.blockId} from={l.from} to={l.to} />
          <span><MathText text={l.text} /></span>
        </div>
      ))}
    </div>
  );
}

/** The turn as it is kept with the conversation: without the drawing (it can be large). */
export function figureTurnForStorage(turn: FigureTurn): FigureTurn {
  return { ...turn, proposal: { ...turn.proposal, svg: undefined } };
}

/** What a figure turn shows under its reply. */
export function FigureProposalView({ turn, preamble }: { turn: FigureTurn; preamble: PreambleAccess }) {
  const live = useTikzAsk((s) => (turn.blockId ? s.figures[turn.blockId] : undefined));
  const p = turn.proposal;
  if (p.kind === "draw" || (p.kind === "failed" && !turn.before.trim())) return <DrawingCard turn={turn} preamble={preamble} />;
  if (p.kind === "change" || p.kind === "failed") return <ChangeCard turn={turn} live={live} />;
  if (p.kind === "caption") return <CaptionCard turn={turn} />;
  if (p.kind === "explain") return <ExplainList turn={turn} />;
  return null;
}
